// Pedido a ITSYS con un solo botón (modo prueba: prepara el carrito, NO confirma).
// jvs-app prepara el pedido; la extensión de Chrome «JVS · Pedido ITSYS» hace el resto en itsys.gr con la sesión
// de quien la usa. Ver backend/services/pedido_itsys y extension-itsys/.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Clock, Download, Loader2, PackageX, Puzzle, ShoppingCart, XCircle } from 'lucide-react'
import api from '@/lib/api'

interface ItemNecesidad {
  reference: string
  supplier_reference: string | null
  quantity_needed: number
  description: string
}

interface Linea {
  id: number
  codart: string
  descripcion: string | null
  codigo: string | null
  ref_informe: string | null
  necesita: number
  descontado: number
  stock_directo: number | null
  pedir: number
  pendiente: number
  estado: string
  origen: string | null
  en_carrito: number | null
}

interface Evento { id: number; t: string; paso: string; estado: string; texto: string }

interface Detalle {
  pedido: { id: number; estado: string; paso: string; creado_at: string; usuario: string
            resultado: { coincide: boolean; n_ok: number; n_esperadas: number; diferencias: { tipo: string; codigo?: string; texto?: string }[] } | null }
  lineas: Linea[]
  eventos: Evento[]
  resumen: { lineas: number; uds_pedir: number; uds_pendientes: number }
}

const ESTADO: Record<string, { texto: string; cls: string }> = {
  ok: { texto: 'Con stock', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900' },
  ok_catalogo: { texto: 'Con stock (catálogo)', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900' },
  poco_stock: { texto: 'Poco stock', cls: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900' },
  sin_stock: { texto: 'Sin stock: espera', cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900' },
  stock_desconocido: { texto: 'Stock desconocido', cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900' },
  por_confirmar: { texto: 'Por confirmar', cls: 'bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:ring-pink-900' },
  pendiente_stock: { texto: 'Mirando stock', cls: 'bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-300 dark:ring-slate-700' },
  nada_que_pedir: { texto: 'Ya pedido', cls: 'bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-300 dark:ring-slate-700' },
}
const EN_MARCHA = ['preparado', 'en_curso']

function extensionInstalada(): string | null {
  return document.documentElement.dataset.jvsPedidoItsys || null
}

export default function PedidoItsysPanel({ items }: { items: ItemNecesidad[] }) {
  const [ext, setExt] = useState<string | null>(extensionInstalada())
  const [pedidoId, setPedidoId] = useState<number | null>(null)
  const [preparando, setPreparando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {   // la extensión marca la página al cargar; por si llega después
    const t = setInterval(() => setExt(extensionInstalada()), 2000)
    return () => clearInterval(t)
  }, [])

  const { data: detalle } = useQuery({
    queryKey: ['pedido-itsys', pedidoId],
    queryFn: async () => (await api.get<Detalle>(`/pedido-itsys/pedidos/${pedidoId}`)).data,
    enabled: pedidoId !== null,
    refetchInterval: (q) => (q.state.data && !EN_MARCHA.includes(q.state.data.pedido.estado) ? false : 3000),
  })

  const lineas = items
    .filter((i) => i.quantity_needed > 0 && i.reference)
    .map((i) => ({ jvs_reference: i.reference, supplier_reference: i.supplier_reference || '',
                   quantity: Math.ceil(i.quantity_needed), description: i.description }))

  const pedir = async () => {
    setError(null)
    setPreparando(true)
    try {
      const { data } = await api.post<{ pedido_id: number; token: string }>('/pedido-itsys/preparar', { items: lineas, modo: 'carrito' })
      setPedidoId(data.pedido_id)
      const id = `p${data.pedido_id}`
      const respuesta = new Promise<{ ok: boolean; error?: string }>((resolve) => {
        const fin = setTimeout(() => resolve({ ok: false, error: 'La extensión no ha respondido. ¿Está instalada y activada en este Chrome?' }), 15000)
        const oye = (ev: MessageEvent) => {
          if (ev.source !== window || ev.data?.fuente !== 'jvs-pedido-itsys' || ev.data?.id !== id) return
          clearTimeout(fin); window.removeEventListener('message', oye); resolve(ev.data)
        }
        window.addEventListener('message', oye)
      })
      window.postMessage({ fuente: 'jvs-app', tipo: 'pedir-itsys', token: data.token, id }, window.location.origin)
      const r = await respuesta
      if (!r.ok) setError(r.error || 'La extensión no ha podido empezar')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo preparar el pedido')
    } finally {
      setPreparando(false)
    }
  }

  const p = detalle?.pedido
  const grupos = (estados: string[]) => (detalle?.lineas || []).filter((l) => estados.includes(l.estado))

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-indigo-600" /> Pedir a ITSYS automáticamente
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">prueba</span>
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Prepara el carrito de ITSYS: stock en directo, CSV con lo que hay, ajuste de las líneas con *** y comparación.
            <b> No pasa al pago ni confirma.</b> El carrito de ITSYS tiene que estar vacío.
          </p>
        </div>
        {ext ? (
          <button onClick={pedir} disabled={preparando || lineas.length === 0 || (!!p && EN_MARCHA.includes(p.estado))}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
            {preparando ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
            Pedir a ITSYS ({lineas.length} líneas)
          </button>
        ) : (
          <a href="/api/pedido-itsys/extension.zip"
             className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            <Download className="w-4 h-4" /> Instalar la extensión
          </a>
        )}
      </div>
      {!ext && (
        <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-2">
          <Puzzle className="w-4 h-4" /> Falta la extensión «JVS · Pedido ITSYS» en este Chrome (descomprimir y «Cargar descomprimida» en chrome://extensions).
        </p>
      )}
      {error && <p className="text-sm text-rose-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</p>}

      {p && detalle && (
        <div className="space-y-3">
          <div className={`rounded-xl border p-3 text-sm ${p.estado === 'carrito_listo' ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30'
                            : p.estado === 'parado' ? 'border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30'
                            : 'border-slate-200 dark:border-slate-700'}`}>
            <div className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              {EN_MARCHA.includes(p.estado) && <><Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> En marcha en itsys.gr…</>}
              {p.estado === 'carrito_listo' && <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Carrito preparado: {p.resultado?.n_ok} de {p.resultado?.n_esperadas} líneas, igual que el pedido</>}
              {p.estado === 'parado' && <><XCircle className="w-4 h-4 text-rose-600" /> Parado — no se ha confirmado nada</>}
            </div>
            <div className="text-slate-600 dark:text-slate-300 mt-1">
              Pedido #{p.id} · {detalle.resumen.uds_pedir} uds al carrito · {detalle.resumen.uds_pendientes} uds quedan pendientes
            </div>
            {p.resultado && !p.resultado.coincide && (
              <ul className="mt-2 space-y-0.5 text-rose-700 dark:text-rose-300">
                {p.resultado.diferencias.slice(0, 10).map((d, i) => <li key={i}>· {d.tipo} {d.codigo || d.texto || ''}</li>)}
              </ul>
            )}
          </div>

          {[{ t: 'Por confirmar (no se piden: falta su código de ITSYS)', e: ['por_confirmar'], icon: AlertTriangle },
            { t: 'Sin stock: esperan al próximo pedido', e: ['sin_stock', 'stock_desconocido'], icon: PackageX },
            { t: 'Poco stock: se pide lo que hay', e: ['poco_stock'], icon: Clock }].map((g) => {
            const ls = grupos(g.e)
            if (!ls.length) return null
            return (
              <div key={g.t}>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-1"><g.icon className="w-4 h-4" /> {g.t} ({ls.length})</div>
                <div className="space-y-1">
                  {ls.map((l) => (
                    <div key={l.id} className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm">
                      <span className="font-mono font-semibold w-28">{l.codart}</span>
                      <span className="flex-1 min-w-[180px] text-slate-600 dark:text-slate-300">{l.descripcion}
                        <span className="block text-xs text-slate-400">{l.codigo ? `ITSYS ${l.codigo}` : l.origen}</span></span>
                      <span className="tabular-nums">{l.estado === 'poco_stock' ? `${l.pedir} de ${l.necesita - l.descontado}` : `× ${l.pendiente}`}</span>
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ring-1 ${(ESTADO[l.estado] || ESTADO.pendiente_stock).cls}`}>{(ESTADO[l.estado] || { texto: l.estado }).texto}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-500">Pasos ({detalle.eventos.length})</summary>
            <ul className="mt-2 space-y-0.5 text-slate-600 dark:text-slate-300">
              {detalle.eventos.map((e) => (
                <li key={e.id} className={e.estado === 'error' ? 'text-rose-600' : ''}>
                  <span className="tabular-nums text-slate-400">{e.t.slice(11, 19)}</span> · {e.texto}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  )
}
